// src/disc/DiscTeamDiamond.js
import React, { useMemo } from "react";
import { Box, Typography, Chip } from "@mui/material";
import { useTranslation } from 'react-i18next';

/**
 * Mapeo de todas las zonas DISC a sus coordenadas en el SVG
 * ViewBox del SVG: 896.14 x 896.14
 */
const DISC_ZONES = {
  // Esquinas principales (1 letra)
  'D': { x: 749.99, y: 137.38 },
  'I': { x: 774.77, y: 809.93 },
  'S': { x: 85.16, y: 809.93 },
  'C': { x: 80.25, y: 137.38 },

  // Combinaciones de 2 letras
  'DI': { x: 754.9, y: 303.55 },
  'DC': { x: 563.45, y: 122.76 },
  'DS': { x: 516.98, y: 373.81 },
  'ID': { x: 754.9, y: 599.43 },
  'IS': { x: 563.45, y: 792.74 },
  'IC': { x: 516.98, y: 549.39 },
  'SI': { x: 280.96, y: 792.74 },
  'SD': { x: 341.62, y: 549.39 },
  'SC': { x: 85.16, y: 599.43 },
  'CS': { x: 85.16, y: 303.55 },
  'CD': { x: 280.96, y: 122.76 },
  'CI': { x: 341.62, y: 373.81 },

  // Combinaciones de 3 letras
  'DIS': { x: 670.79, y: 414.99 },
  'DIC': { x: 626.29, y: 316.69 },
  'DCS': { x: 474.51, y: 208.92 },
  'DCI': { x: 555.91, y: 264.39 },
  'DSI': { x: 563.45, y: 435.63 },
  'DSC': { x: 452.45, y: 322.17 },
  'IDS': { x: 626.29, y: 599.43 },
  'ICD': { x: 563.45, y: 484.37 },
  'ISC': { x: 474.51, y: 712.1 },
  'ISD': { x: 555.91, y: 661.74 },
  'ICS': { x: 462.45, y: 605.25 },
  'IDC': { x: 670.79, y: 505.29 },
  'SID': { x: 367.12, y: 712.1 },
  'SDI': { x: 391.07, y: 605.25 },
  'SIC': { x: 271.96, y: 661.74 },
  'SCI': { x: 213.03, y: 599.43 },
  'SDC': { x: 280.96, y: 484.37 },
  'SCD': { x: 165.96, y: 505.29 },
  'CID': { x: 393.07, y: 322.17 },
  'CDI': { x: 367.12, y: 208.92 },
  'CIS': { x: 280.96, y: 427.38 },
  'CSI': { x: 167.96, y: 414.99 },
  'CDS': { x: 271.96, y: 264.39 },
  'CSD': { x: 213.03, y: 316.69 },
};

// Paleta de colores para diferenciar miembros del equipo
const TEAM_COLORS = [
  '#3b82f6', // Azul
  '#ef4444', // Rojo
  '#10b981', // Verde
  '#f59e0b', // Naranja
  '#8b5cf6', // Púrpura
  '#ec4899', // Rosa
  '#14b8a6', // Teal
  '#f97316', // Naranja oscuro
  '#6366f1', // Índigo
  '#84cc16', // Lima
];

/**
 * Determina el código DISC (ej: "DIS", "DI") basado en los puntajes
 * @param {Object} disc - {D, I, S, C} con valores numéricos
 * @returns {string} - Código DISC (ej: "DIS", "DI", "D")
 */
function getDiscCode(disc) {
  if (!disc) return null;

  const { D = 0, I = 0, S = 0, C = 0 } = disc;

  // Crear array de rasgos ordenados por puntaje (mayor a menor)
  const traits = [
    { letter: 'D', score: D },
    { letter: 'I', score: I },
    { letter: 'S', score: S },
    { letter: 'C', score: C }
  ].sort((a, b) => b.score - a.score);

  // Si los 2 primeros suman 75% o más, usar 2 letras
  const top2Sum = traits[0].score + traits[1].score;

  if (top2Sum >= 75) {
    return traits[0].letter + traits[1].letter;
  } else {
    // Si no, usar 3 letras
    return traits[0].letter + traits[1].letter + traits[2].letter;
  }
}

/**
 * Obtiene las coordenadas para un perfil DISC
 * @param {Object} disc - {D, I, S, C}
 * @returns {Object} - {x, y} coordenadas en el SVG
 */
function getDiscPosition(disc) {
  const code = getDiscCode(disc);
  const position = DISC_ZONES[code];

  if (!position) {
    console.warn(`No se encontró posición para el código DISC: ${code}`);
    return { x: 448.07, y: 448.07 }; // Centro por defecto
  }

  return position;
}

/**
 * Componente para mostrar el diamante DISC con múltiples miembros del equipo
 * Muestra Natural (círculo azul) -> Adaptado (círculo negro) con flecha coloreada
 * 
 * Props:
 * - BaseSvg: componente React del SVG base (via SVGR)
 * - teamMembers: Array de objetos con { name, natural: {D, I, S, C}, adapted: {D, I, S, C} }
 */
export default function DiscTeamDiamond({ BaseSvg, teamMembers = [] }) {
  const VIEWBOX = 896.14;
  const { t } = useTranslation();

  // Calcular posiciones para cada miembro (natural y adaptado)
  const membersWithPositions = useMemo(() => {
    return teamMembers.map((member, index) => {
      const naturalPos = getDiscPosition(member.natural);
      const adaptedPos = getDiscPosition(member.adapted);
      const color = TEAM_COLORS[index % TEAM_COLORS.length];

      return {
        ...member,
        naturalPos,
        adaptedPos,
        color,
        index: index + 1
      };
    });
  }, [teamMembers]);

  return (
    <Box sx={{ width: "100%" }}>
      {/* Diamante con puntos y flechas */}
      <Box sx={{ position: "relative", width: "100%", maxWidth: 520, mx: "auto", mb: 3 }}>
        {/* SVG base, sin los rótulos de las zonas (C, CD, CDI…): el mapa del equipo va sin letras */}
        <Box sx={{ width: "100%", lineHeight: 0, '& text': { display: 'none' } }}>
          <BaseSvg width="100%" height="100%" viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} />
        </Box>

        {/* Overlay con Natural -> Adaptado para cada miembro */}
        <svg
          viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
          width="100%"
          height="100%"
          style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
        >
          {/* Definir markers de flecha para cada color */}
          <defs>
            {membersWithPositions.map((member) => (
              <marker
                key={`arrow-${member.index}`}
                id={`arrow-${member.index}`}
                markerWidth="12"
                markerHeight="12"
                refX="7"
                refY="3.5"
                orient="auto"
              >
                <path d="M0,0 L0,7 L7,3.5 z" fill={member.color} />
              </marker>
            ))}
          </defs>

          {membersWithPositions.map((member) => (
            <g key={member.index}>
              {/* Círculo Natural (color del miembro) */}
              <circle
                cx={member.naturalPos.x}
                cy={member.naturalPos.y}
                r={14}
                fill={member.color}
                stroke="white"
                strokeWidth="2"
              />

              {/* Flecha Natural -> Adaptado (color del miembro) */}
              <line
                x1={member.naturalPos.x}
                y1={member.naturalPos.y}
                x2={member.adaptedPos.x}
                y2={member.adaptedPos.y}
                stroke={member.color}
                strokeWidth="4"
                markerEnd={`url(#arrow-${member.index})`}
              />

              {/* Círculo Adaptado (color del miembro) */}
              <circle
                cx={member.adaptedPos.x}
                cy={member.adaptedPos.y}
                r={14}
                fill={member.color}
                stroke="white"
                strokeWidth="2"
              />
              
              {/* Número en el círculo adaptado */}
              <text
                x={member.adaptedPos.x}
                y={member.adaptedPos.y}
                textAnchor="middle"
                dominantBaseline="central"
                fill="white"
                fontSize="16"
                fontWeight="bold"
              >
                {member.index}
              </text>
            </g>
          ))}
        </svg>
      </Box>

      {/* Leyenda de miembros */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          {t('disc.discComparisons.teamMembersLegend')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {membersWithPositions.map((member) => (
            <Chip
              key={member.index}
              label={`${member.index}. ${member.name}`}
              sx={{
                backgroundColor: member.color,
                color: 'white',
                fontWeight: 500,
                '& .MuiChip-label': {
                  px: 1.5
                }
              }}
            />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {t('disc.discComparisons.teamMapLegendHint')}
        </Typography>
      </Box>
    </Box>
  );
}